import { SolanaProvider } from "@uxuycom/web3-tg-sdk/dist/types/src/provider/SolanaProvider/index";
import ConnectionWrapper, {
    CHAIN_IDS,
    PROVIDER_ALLIANCE,
} from "./ConnectionWrapper";
import { CreateOrder } from "~/types";
import { getChainKey, getContract } from "./ChainKeyMaps";

class SolanaConnectionWrapper implements ConnectionWrapper {
    address: string | null = null;
    addressType = 3;
    chainId = String(CHAIN_IDS.SOLANA);
    alliance = PROVIDER_ALLIANCE.SOL;

    constructor(public provider: SolanaProvider) {
        this.address = this.provider.publicKey?.toString() ?? null;
    }

    async getAddress(): Promise<string | null | undefined> {
        return this.provider
            .getAccount()
            .then((address) => {
                const fixedAddress = address as unknown as string;
                this.address = fixedAddress;
                return fixedAddress;
            })
            .catch((err) => {
                this.address = null;
                throw err;
            });
    }

    async getAddressSilently(): Promise<string | null | undefined> {
        this.address = this.provider.publicKey?.toString() ?? null;
        return this.address;
    }

    async connect(): Promise<void> {
        await this.provider.connect({}, false);
        return;
    }

    isConnected(): boolean {
        return this.address != null && this.address.length > 0;
    }

    async disconnect(): Promise<void> {
        this.address = null;
        return await this.provider.disconnect();
    }

    async sendTransaction(
        _chain_id: string,
        tradeOrder: CreateOrder
    ): Promise<any> {
        const fromAddress = this.address;

        if (!fromAddress || fromAddress.length === 0) return;

        const chainId = tradeOrder.swap_info.from.chain_id;
        const chainKey = getChainKey(chainId);
        const fromTokenAddress = getContract(
            tradeOrder.swap_info.from.contract
        );
        const toTokenAddress = getContract(tradeOrder.swap_info.to.contract);
        const order_id = tradeOrder.order_id;
        const tx = await this.provider.request({
            method: "signTransaction",
            params: {
                message: tradeOrder.transaction_data,
            },
            options: {
                ext_payload: {
                    method: "wallet_tokenFollow",
                    params: [chainKey, [fromTokenAddress, toTokenAddress]],
                },
                ext_view_info: { ...tradeOrder.swap_info, order_id },
            },
        });

        return tx.serialize();
    }

    async getTransactionByHash(_hash: string) {
        return _hash;
    }

    async waitForTransaction(
        hash: string,
        timeout = 1500,
        count = 3
    ): Promise<any> {
        const result = await this.getTransactionByHash(hash);
        if (result) return result;

        if (count <= 0) {
            return result;
        }

        await new Promise((resolve) => setTimeout(resolve, timeout));
        return this.waitForTransaction(hash, timeout, count - 1);
    }
}

export default SolanaConnectionWrapper;
